import React from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { proposeSources, updateSource, setSourceChecks, deleteSourceRecord,
  regenerateConcept, recheckSources } from './api';

const CHECK_LABELS = {
  check_official: 'Official source',
  check_current: 'Current / not superseded',
  check_jurisdiction: 'Jurisdiction fit',
  check_topic: 'Topic fit',
  check_effective_date: 'Effective date',
  check_conflict: 'No conflict w/ ZU',
};
const dot = (v) => (v === 'pass' ? 'bg-green-500' : v === 'fail' ? 'bg-red-500' : 'bg-gray-400');

// A source nobody has ruled on yet is the only kind that needs the full six-check
// form open. Once it is verified or failed the decision is made, so the row
// collapses to one line and the panel stays readable: a concept commonly carries
// twenty-plus sources, and rendering every one of them expanded turned this
// inspector into a wall of dropdowns taller than the concept it describes.
const isDecided = (r) => r.verification_status === 'verified' || r.verification_status === 'failed';

// A URL that did not resolve when it was last checked. Proposed sources are AI
// suggestions, and an unreachable link is the single most common reason a reviewer
// fails one - so say it on the row instead of making them click to find out.
const linkDead = (r) => r.url_status != null && (r.url_status === 0 || r.url_status >= 400);

const verdictTone = (r) => (r.verification_status === 'verified' ? 'text-green-400'
  : r.verification_status === 'failed' ? 'text-[#ffb4b4]' : 'text-[#f7a23b]');

// The ▾/▸ text glyphs rendered around 7px and were nearly invisible next to 10px labels,
// which made the one control on every row hard to see and harder to hit. An SVG scales
// with its box, so the target is a real 20px square.
const Chevron = ({ open, className = '' }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
    className={`w-5 h-5 shrink-0 transition-transform ${open ? 'rotate-90' : ''} ${className}`}>
    <path d="M7.5 5.5 L13 10 L7.5 14.5 Z" />
  </svg>
);

function SourceRow({ r, isDirector, review, expanded, onToggle, onChanged }) {
  const [notes, setNotes] = React.useState(r.notes ?? '');

  const setCheck = useMutation({
    mutationFn: ({ field, value }) => updateSource(r.id, { [field]: value }),
    onSuccess: onChanged,
  });
  const setAll = useMutation({
    mutationFn: ({ value, withNotes }) => setSourceChecks(r.id, value, withNotes ? { notes: withNotes } : {}),
    onSuccess: (res) => {
      toast.success(res?.source_record?.verification_status === 'verified'
        ? 'Source corroborated' : 'Source marked failed');
      onChanged();
    },
    onError: (e) => toast.error(e?.message || 'Could not record the checks'),
  });
  const saveNote = useMutation({
    mutationFn: () => updateSource(r.id, { notes }),
    onSuccess: () => { toast.success('Note saved'); onChanged(); },
    onError: (e) => toast.error(e?.message || 'Could not save the note'),
  });
  const remove = useMutation({
    mutationFn: () => deleteSourceRecord(r.id),
    onSuccess: () => { toast.success('Source removed'); onChanged(); },
    onError: (e) => toast.error(e?.message || 'Could not remove the source'),
  });

  const undecided = review?.undecided_ids?.includes(r.id);

  return (
    <div className={`mb-2 pb-2 border-b border-[#161e2a] last:border-0 ${
      undecided ? 'border-l-2 border-l-[#f7a23b] pl-2' : ''}`}>
      <div className="flex items-start gap-2">
        <button onClick={onToggle}
          className="text-[#7d8aa0] hover:text-[#c7d0e0] shrink-0 -ml-1 p-0.5 rounded"
          aria-label={expanded ? 'Collapse source' : 'Expand source'}
          aria-expanded={expanded}>
          <Chevron open={expanded} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-[#c7d0e0]">
            {r.source_url
              ? <a href={r.source_url} target="_blank" rel="noreferrer"
                  className={`underline ${linkDead(r) ? 'text-[#ffb4b4]' : 'text-[#5ea9ff]'}`}>
                  {r.source_title}
                </a>
              : r.source_title}
            {r.source_organization && <span className="text-[#7d8aa0]"> · {r.source_organization}</span>}
          </div>
          <div className="flex items-center gap-2 text-[11px] mt-0.5 flex-wrap">
            <span className={verdictTone(r)}>{String(r.verification_status || '').replace(/_/g, ' ')}</span>
            {linkDead(r) && (
              <span className="text-[#ffb4b4]" title={`HTTP ${r.url_status || 'unreachable'}`}>
                · link not reachable ({r.url_status || 'no response'})
              </span>
            )}
            {!expanded && r.notes && <span className="text-[#5f6b7e] truncate">· {r.notes}</span>}
          </div>
        </div>
        {isDirector && review?.removable && (
          <button
            onClick={() => { if (window.confirm(`Remove "${r.source_title}" from this concept's sources?`)) remove.mutate(); }}
            disabled={remove.isPending}
            className="text-[11px] text-[#ffb4b4] disabled:opacity-40 shrink-0">Remove</button>
        )}
      </div>

      {expanded && (
        <div className="mt-2 pl-5">
          <div className="flex items-center gap-2 mb-1">
            <button onClick={() => setAll.mutate({ value: 'pass' })} disabled={setAll.isPending}
              className="text-[11px] px-2 py-0.5 rounded bg-[#12251a] border border-[#2b4d38] text-green-400 disabled:opacity-40">
              Pass all
            </button>
            <button onClick={() => setAll.mutate({ value: 'fail', withNotes: notes })} disabled={setAll.isPending}
              className="text-[11px] px-2 py-0.5 rounded bg-[#2a1414] border border-[#5c2b2b] text-[#ffb4b4] disabled:opacity-40">
              Fail all
            </button>
          </div>
          {Object.entries(CHECK_LABELS).map(([field, label]) => (
            <div key={field} className="flex items-center justify-between text-[11px] my-1 text-[#c7d0e0]">
              <span>{label}</span>
              <span className="flex items-center gap-2">
                <select value={r.checks[field]}
                  onChange={(e) => setCheck.mutate({ field, value: e.target.value })}
                  className="bg-[#0f1620] border border-[#3a4a5f] rounded px-1 py-0.5 text-[11px] text-[#e6ecf5] [&>option]:bg-[#0f1620] [&>option]:text-[#e6ecf5]">
                  {['pass', 'fail', 'unknown'].map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
                <span className={`w-2.5 h-2.5 rounded-full ${dot(r.checks[field])}`} />
              </span>
            </div>
          ))}
          <div className="flex items-center gap-2 mt-1">
            <input value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Note (optional): why this passed or failed"
              className="flex-1 bg-transparent border border-[#2b3a4d] rounded px-2 py-1 text-[11px] text-[#c7d0e0]" />
            {/* Compared against the saved note, not against empty: requiring a
                non-empty value meant clearing a note hid the button and the old
                text stayed on the server. */}
            {notes !== (r.notes ?? '') && (
              <button onClick={() => saveNote.mutate()} disabled={saveNote.isPending}
                className="text-[11px] text-[#5ea9ff] disabled:opacity-40">Save note</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SourceConfidencePanel({ concept, conceptId, isDirector, onChanged }) {
  const c = concept;
  const review = c.source_review;
  const records = c.source_records || [];

  const propose = useMutation({
    mutationFn: () => proposeSources(conceptId),
    onSuccess: onChanged,
    onError: (e) => toast.error(e?.message || 'Could not propose sources'),
  });
  const recheck = useMutation({
    mutationFn: () => recheckSources(conceptId),
    onSuccess: (res) => {
      const dead = res?.unreachable ?? 0;
      toast.success(dead > 0
        ? `Rechecked. ${dead} link${dead === 1 ? '' : 's'} did not resolve.`
        : 'Rechecked. Every link resolved.');
      onChanged();
    },
    onError: (e) => toast.error(e?.message || 'Could not recheck the links'),
  });
  const regenerate = useMutation({
    mutationFn: () => regenerateConcept(conceptId),
    onSuccess: () => { toast.success('JPRO rewrote the concept - review it and confirm its sources'); onChanged(); },
    onError: (e) => toast.error(e?.message || 'Could not regenerate the concept'),
  });

  const verified = records.filter((r) => r.verification_status === 'verified').length;
  const failed = records.filter((r) => r.verification_status === 'failed').length;
  const open = records.length - verified - failed;
  const dead = records.filter(linkDead).length;

  // Open by default. This panel had to fold itself away when it was one section of a
  // ten-section inspector scroll; it is a stage of its own now, so the list of sources IS
  // the page and hiding it behind a chevron meant arriving at Sources and seeing nothing.
  // Still collapsible, for skimming past it once the work is done.
  const [sectionOpen, setSectionOpen] = React.useState(true);
  // Rows the reviewer has explicitly toggled. Everything else follows the default
  // (undecided open, decided closed), so a decision made in this session collapses
  // the row on its own without fighting a manual choice.
  const [overrides, setOverrides] = React.useState({});
  const rowOpen = (r) => (r.id in overrides ? overrides[r.id] : !isDecided(r));

  return (
    <div className="pb-4 mb-4 border-b border-[#1d2634]">
      <button onClick={() => setSectionOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-3 text-left mb-3"
        aria-expanded={sectionOpen}>
        <span className="flex items-center gap-1.5 min-w-0">
          <Chevron open={sectionOpen} className="text-[#7d8aa0]" />
          <span className="text-xs font-semibold text-[#c7d0e0]">
            Source confidence
            <span className="text-[#7d8aa0] font-normal">
              {' · '}{String(c.source_confidence || '').replace(/_/g, ' ')}
            </span>
          </span>
        </span>
        {/* Counts as pills at a legible size. They were a 10px run of text crammed against
            the chevron, which is the least readable place to put the summary of a panel
            somebody opens specifically to read the summary. */}
        <span className="flex items-center gap-1.5 flex-wrap justify-end shrink-0">
          {verified > 0 && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#0b2a17] text-green-400 border border-[#1c4a30]">
              {verified} verified
            </span>
          )}
          {open > 0 && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#3a2a12] text-[#f7a23b] border border-[#6b4516]">
              {open} to check
            </span>
          )}
          {failed > 0 && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#2a1414] text-[#ffb4b4] border border-[#5c2b2b]">
              {failed} failed
            </span>
          )}
          {dead > 0 && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#2a1414] text-[#ffb4b4] border border-[#5c2b2b]">
              {dead} dead link{dead === 1 ? '' : 's'}
            </span>
          )}
          {records.length === 0 && (
            <span className="text-[11px] text-[#7d8aa0]">no sources yet</span>
          )}
        </span>
      </button>

      {/* The reason approval is blocked stays visible even when the list is folded,
          so collapsing can never hide the thing that needs acting on. */}
      {review?.blocked && review?.reason && (
        <div className="mb-2">
          <div className="text-[11px] text-[#f7a23b]">⚠ {review.reason}</div>
          {review.suggest_regenerate && (
            <div className="flex items-center gap-2 mt-1">
              <button onClick={() => propose.mutate()} disabled={propose.isPending}
                className="text-[11px] px-2 py-1 rounded bg-[#1f2a38] border border-[#2b3a4d] text-[#5ea9ff] disabled:opacity-40">
                Propose new sources
              </button>
              {review.can_regenerate && (
                <button
                  onClick={() => { if (window.confirm('Rewrite this concept from its original request? The current wording will be replaced.')) regenerate.mutate(); }}
                  disabled={regenerate.isPending}
                  className="text-[11px] px-2 py-1 rounded bg-[#1f2a38] border border-[#2b3a4d] text-[#5ea9ff] disabled:opacity-40">
                  {regenerate.isPending ? 'Regenerating…' : 'Regenerate the concept'}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {sectionOpen && (
        <>
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => propose.mutate()} disabled={propose.isPending}
              className="text-[11px] text-[#4aa3ff] disabled:opacity-40">
              {records.length === 0
                ? '+ Propose sources (JPRO Intelligence)'
                : '+ Propose more sources'}
            </button>
            {records.length > 0 && (
              <button onClick={() => recheck.mutate()} disabled={recheck.isPending}
                className="text-[11px] text-[#4aa3ff] disabled:opacity-40">
                {recheck.isPending ? 'Rechecking…' : 'Recheck links'}
              </button>
            )}
          </div>
          {records.map((r) => (
            <SourceRow key={r.id} r={r} isDirector={isDirector} review={review}
              expanded={rowOpen(r)}
              onToggle={() => setOverrides((o) => ({ ...o, [r.id]: !rowOpen(r) }))}
              onChanged={onChanged} />
          ))}
        </>
      )}
    </div>
  );
}
